import { useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/providers/AuthProvider'
import { usersApi } from '@/api/usersApi'
import { ssoErrorMessage, startSso } from '@/lib/sso'
import { getSession } from '@/lib/authToken'
import { takeSessionEndedReason } from '@/lib/idleActivity'
import { takeRestoreTarget } from '@/lib/lastLocation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PasswordInput } from '@/components/ui/password-input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'

interface LocationState {
  from?: { pathname?: string }
  // Set when returning from the password-reset flow so we can confirm success.
  resetSuccess?: boolean
}

// While a freshly-created user signs in for the first time, Cognito requires a
// new password before issuing tokens.
interface Challenge {
  email: string
  session: string
}

export function LoginPage() {
  const { login, completeNewPassword } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const redirectTo = (location.state as LocationState | null)?.from?.pathname ?? '/'
  const resetSuccess = (location.state as LocationState | null)?.resetSuccess ?? false

  // Read once, on mount, and consumed as it is read: the reason explains the
  // sign-out that just happened, so it must not caption later visits. A lazy
  // initialiser rather than an effect, because a 401 arrives here via a full
  // page load (window.location.assign), so there is exactly one chance to read
  // it before the first paint.
  const [endedReason] = useState(() => takeSessionEndedReason())
  // Set by the backend's SSO redirects (/auth/sso/start) and the callback page.
  const [params] = useSearchParams()
  const ssoError = ssoErrorMessage(params.get('error'))
  const ssoNext = redirectTo !== '/' ? redirectTo : undefined

  const [email, setEmail] = useState('')
  // Email first: the password step opens once the identifier resolves to a password account.
  const [resolved, setResolved] = useState<{ email: string; idp?: string } | null>(null)
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [challenge, setChallenge] = useState<Challenge | null>(null)

  async function handleContinue(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const result = await usersApi.resolveLogin(email.trim())
      if (result.method === 'sso') {
        startSso({ email: result.email, next: ssoNext })
        return
      }
      setResolved({ email: result.email, idp: result.idp })
      setSubmitting(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed')
      setSubmitting(false)
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!resolved) return
    setError(null)
    setSubmitting(true)
    try {
      const result = await login(resolved.email, password)
      // The browser is already on its way to the IdP.
      if (result.status === 'sso') return
      if (result.status === 'new_password_required') {
        setChallenge({ email: result.email, session: result.session })
        setPassword('')
      } else {
        // An explicit `from` (the route guard bounced them here mid-navigation)
        // is more specific than a remembered location, so it wins. Otherwise
        // return them to the screen their last session ended on.
        if (redirectTo !== '/') {
          navigate(redirectTo, { replace: true })
          return
        }
        const target = await takeRestoreTarget(getSession())
        navigate(target.href, {
          replace: true,
          state: target.message ? { restoreNotice: target.message } : undefined,
        })
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign in failed'
      // A disabled account → industry-standard "no access" page.
      if (/disabled/i.test(message)) {
        navigate('/no-access', { replace: true })
        return
      }
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className='flex min-h-screen items-center justify-center bg-muted/30 px-4'>
      <Card className='w-full max-w-sm'>
        <CardHeader className='items-center text-center'>
          <img
            src='/images/title21-lockup.png'
            alt='Title21'
            width={1322}
            height={336}
            className='mb-2 h-auto w-72'
          />
          <CardTitle className='text-xl'>
            {challenge ? 'Set a new password' : 'Admin sign in'}
          </CardTitle>
          <CardDescription>
            {challenge
              ? 'Your account requires a new password before you can continue.'
              : 'Sign in to the CGT administrative dashboard'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {challenge ? (
            <NewPasswordForm
              onCancel={() => {
                setChallenge(null)
                setError(null)
              }}
              onSubmit={async (newPassword) => {
                setError(null)
                setSubmitting(true)
                try {
                  await completeNewPassword(challenge.email, newPassword, challenge.session)
                  navigate(redirectTo, { replace: true })
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Could not set password')
                } finally {
                  setSubmitting(false)
                }
              }}
              submitting={submitting}
              error={error}
            />
          ) : (
            <form onSubmit={resolved ? handleSubmit : handleContinue} className='flex flex-col gap-4'>
              {/*
                Without this the user sees a bare login form and reads it as the
                app having logged them out at random, or as a crash.
              */}
              {endedReason === 'inactivity' && (
                <p className='rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800' role='status'>
                  Your session ended because you were inactive. Sign in to continue where you left
                  off.
                </p>
              )}
              {resetSuccess && (
                <p
                  className='rounded-md bg-green-50 px-3 py-2 text-sm text-green-700'
                  role='status'
                >
                  Password reset — sign in with your new password.
                </p>
              )}
              <div className='flex flex-col gap-1.5'>
                <Label htmlFor='email'>Email or username</Label>
                <Input
                  id='email'
                  type='text'
                  autoComplete='username'
                  required
                  readOnly={!!resolved}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {resolved && (
                  <button
                    type='button'
                    className='self-end text-sm text-muted-foreground hover:underline'
                    onClick={() => {
                      setResolved(null)
                      setPassword('')
                      setError(null)
                    }}
                  >
                    Use a different account
                  </button>
                )}
              </div>
              {resolved && (
                <>
                  <div className='flex flex-col gap-1.5'>
                    <Label htmlFor='password'>Password</Label>
                    <PasswordInput
                      id='password'
                      autoComplete='current-password'
                      required
                      autoFocus
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                  <Link
                    to='/reset-password'
                    className='-mt-1 self-end text-sm text-muted-foreground hover:underline'
                  >
                    Forgot your password?
                  </Link>
                </>
              )}
              {(error ?? ssoError) && (
                <p className='text-sm text-destructive' role='alert'>
                  {error ?? ssoError}
                </p>
              )}
              <Button type='submit' disabled={submitting} className='mt-2'>
                {resolved ? (submitting ? 'Signing in…' : 'Sign in') : submitting ? 'Checking…' : 'Continue'}
              </Button>
              {resolved?.idp && (
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => startSso({ email: resolved.email, idp: resolved.idp, next: ssoNext })}
                >
                  Sign in with SSO
                </Button>
              )}
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function NewPasswordForm({
  onSubmit,
  onCancel,
  submitting,
  error,
}: {
  onSubmit: (newPassword: string) => void
  onCancel: () => void
  submitting: boolean
  error: string | null
}) {
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)

  function submit(e: FormEvent) {
    e.preventDefault()
    if (next !== confirm) {
      setLocalError('Passwords do not match.')
      return
    }
    setLocalError(null)
    onSubmit(next)
  }

  return (
    <form onSubmit={submit} className='flex flex-col gap-4'>
      <div className='flex flex-col gap-1.5'>
        <Label htmlFor='new-password'>New password</Label>
        <PasswordInput
          id='new-password'
          autoComplete='new-password'
          required
          minLength={8}
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
      </div>
      <div className='flex flex-col gap-1.5'>
        <Label htmlFor='confirm-password'>Confirm new password</Label>
        <PasswordInput
          id='confirm-password'
          autoComplete='new-password'
          required
          minLength={8}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
      </div>
      {(localError || error) && (
        <p className='text-sm text-destructive' role='alert'>
          {localError ?? error}
        </p>
      )}
      <div className='mt-2 flex gap-2'>
        <Button type='button' variant='outline' onClick={onCancel} className='flex-1'>
          Back
        </Button>
        <Button type='submit' disabled={submitting || next.length < 8} className='flex-1'>
          {submitting ? 'Saving…' : 'Set password'}
        </Button>
      </div>
    </form>
  )
}
